import {
  Component,
  DestroyRef,
  Signal,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  untracked
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { SplitAreaComponent, SplitComponent } from 'angular-split';
import {
  catchError,
  combineLatest,
  concatMap,
  filter,
  from,
  map,
  of,
  switchMap,
  take,
  tap,
  toArray
} from 'rxjs';

import { GbeErrorCodes } from '@gfe/core/models/gbe/gbe-error-codes.enum';
import { GbeError } from '@gfe/core/models/gbe/gbe-error.model';
import { DeployChannelCountService } from '@gfe/core/services/deploy-plan-config/deploy-channel-count.service';
import { ICONS } from '@gfe/core/services/icons/icons.service';
import { NotificationService } from '@gfe/core/services/notification/notification.service';
import { selectDataspaceModel } from '@gfe/core/store/dataspace/dataspace.selectors';
import { isNil } from '@gfe/core/utils/shared-utils';
import {
  AvailableFilterField,
  FilterConfiguration
} from '@gfe/shared/components/filter-query-builder/filter.models';
import {
  ModalComponent,
  ModalConfig,
  ModalSize,
  ModalType
} from '@gfe/shared/components/modal/modal.component';
import { StringWithBadgeCellValue } from '@gfe/shared/components/table/table-cell/string-with-badge/string-with-badge-cell.component';
import {
  SortState,
  TableAction,
  TableColumn,
  TableConfig
} from '@gfe/shared/components/table/table.model';
import {
  SearchConfig,
  ToolboxComponent,
  ToolboxSection
} from '@gfe/shared/components/toolbox/toolbox.component';

import { renderTagList } from '@core/models/entities/generic-entity-cfg';
import { OrderBy } from '@core/models/entity/entity-selector-request.model';
import {
  FilterPresetPayload,
  PLANNING_SPACE_PRESET_ENTITY_TYPE
} from '@core/models/filter-preset.model';
import { RawKpi } from '@core/models/kpi.model';
import { DeployStatusOverride, PlanMetadataModel } from '@core/models/plan.model';
import {
  AddPlanningSpaceRequest,
  PlanningSpaceMetadataModel
} from '@core/models/planning-space/planning-space.model';
import {
  FetchPlanningSpaceListResponse,
  PlanningSpaceService
} from '@core/services/planning-space/planning-space.service';
import { RecipeService } from '@core/services/recipes/recipe.service';
import { selectTags } from '@core/store/admin/tags-labels/tags-labels.selectors';
import { selectDeployStatusOverrides } from '@core/store/plan/plan.selectors';
import { selectHideInternalPlanningSpaces } from '@core/store/user-preferences/user-preferences.selectors';
import { isRecipeParameterError } from '@core/utils/recipe-parameter-error.utils';

import { FilterQueryBuilderComponent } from '@shared/components/filter-query-builder/filter-query-builder.component';
import { KpiComparisonComponent } from '@shared/components/kpi-comparison/kpi-comparison.component';
import {
  KpiComparisonChoice,
  KpiComparisonItem
} from '@shared/components/kpi-comparison/kpi-comparison.model';
import { KpiComparisonService } from '@shared/components/kpi-comparison/kpi-comparison.service';
import { PaginationConfig } from '@shared/components/pagination/pagination.component';
import { TableComponent } from '@shared/components/table/table.component';
import {
  addOrReplaceConditionInConfig,
  collectActiveFilterFields,
  createFilterCondition,
  findFilterField,
  pruneUnfilledConditions,
  removeConditionFromConfig
} from '@shared/utils/filter-condition.utils';
import { countFilterConditions } from '@shared/utils/filter-preset.utils';
import { Pagination, PaginationState } from '@shared/utils/pagination.utils';

import { FilterPresetsComponent } from '@features/components/entities/filter-presets/filter-presets.component';
import {
  AppliedFilterPreset,
  FilterPresetsStore
} from '@features/components/entities/filter-presets/filter-presets.store';
import {
  NewPlanningSpaceComponent,
  NewPlanningSpaceConfig,
  NewPlanningSpaceOutputData
} from '@features/components/planning-space-list/new-planning-space/new-planning-space.component';
import {
  PLANNING_SPACE_FILTERABLE_COLUMNS,
  PLANNING_SPACE_SORTABLE_COLUMNS,
  makePlanningSpaceFilterFields,
  makePlanningSpaceSortableFields
} from '@features/components/planning-space-list/planning-space-filter-fields';
import {
  UpdatePlanningSpaceComponent,
  UpdatePlanningSpaceConfig
} from '@features/components/planning-space-list/update-planning-space/update-planning-space.component';

/**
 * What a space's plan list depends on, as far as its list row can tell: adding or deleting
 * a plan changes `nbPlans`, a deploy can move `kpiPlanId`, and editing the space bumps
 * `updatedAt`. A recompute of a plan other than the default shows none of these, so its new
 * KPIs only reach an open comparison once the panel is reopened.
 */
function spacePlansVersion(space: PlanningSpaceMetadataModel): string {
  return `${space.nbPlans}|${space.kpiPlanId ?? ''}|${String(space.updatedAt)}`;
}

@Component({
  selector: 'app-planning-space-list',
  imports: [
    ToolboxComponent,
    TableComponent,
    NewPlanningSpaceComponent,
    ModalComponent,
    UpdatePlanningSpaceComponent,
    FilterQueryBuilderComponent,
    FilterPresetsComponent,
    KpiComparisonComponent,
    SplitComponent,
    SplitAreaComponent
  ],
  providers: [FilterPresetsStore, DeployChannelCountService],
  templateUrl: './planning-space-list.component.html',
  styles: ``
})
export class PlanningSpaceListComponent {
  protected readonly ICONS = ICONS;
  protected readonly ModalSize = ModalSize;

  @ViewChild('newPlanningSpaceComponent')
  newPlanningSpaceComponentRef?: NewPlanningSpaceComponent;

  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly planningSpaceService = inject(PlanningSpaceService);
  private readonly notificationService = inject(NotificationService);
  private deployOverrides = toSignal(this.store.select(selectDeployStatusOverrides), {
    initialValue: {} as Record<string, DeployStatusOverride>
  });
  private readonly recipeService = inject(RecipeService);
  private readonly deployChannels = inject(DeployChannelCountService);
  private readonly filterPresetsStore = inject(FilterPresetsStore);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly selectedItemIds = signal<Set<string>>(new Set<string>());
  private readonly availableTags = this.store.selectSignal(selectTags);
  /**
   * Asks GBE to leave the internal spaces out rather than dropping them from the fetched
   * page: the list is paged server-side, so a client-side drop would render a short page
   * against a `total` that still counted them.
   */
  private readonly hideInternalSpaces = this.store.selectSignal(
    selectHideInternalPlanningSpaces
  );

  protected selectedItems = computed(() => {
    const allSpaces = this.rawPlanningSpaces()?.result ?? [];
    const selectedIds = this.selectedItemIds();
    return new Set(allSpaces.filter((space) => selectedIds.has(space.id)));
  });

  private readonly kpiComparison = inject(KpiComparisonService);
  protected readonly kpiDecimals = this.kpiComparison.decimals;
  protected readonly kpiOrder = this.kpiComparison.order;
  protected readonly compareOpen = signal(false);
  protected readonly canCompare = computed(() => this.selectedItems().size >= 2);
  private readonly destroyRef = inject(DestroyRef);

  /** Plans the user picked per space. A space absent here shows GBE's default, `kpiPlanId`. */
  private readonly comparedPlanIds = signal<ReadonlyMap<string, string>>(new Map());
  /**
   * Each compared space's plans, kept across selection changes because the list refetches on
   * every tick of a checkbox. An entry is refetched once the space's row reports a different
   * `spacePlansVersion`, and the whole cache goes when the comparison closes, so reopening it
   * never shows plans or KPIs older than the list beside it.
   */
  private readonly spacePlans = signal<ReadonlyMap<string, PlanMetadataModel[]>>(new Map());
  /** The `spacePlansVersion` each in-flight or landed fetch was made for. */
  private readonly requestedSpacePlans = new Map<string, string>();

  /**
   * The space-level KPIs the search endpoint already returns.
   *
   * The list is paged server-side and `selectedItems()` only resolves rows on the current
   * page, so paging away empties the comparison — the same limitation the update and delete
   * actions already have.
   */
  protected readonly compareItems = computed<KpiComparisonItem[]>(() => {
    this.currentLang();
    return this.kpiComparison.buildItems(
      Array.from(this.selectedItems()).map((space) => {
        const plans = this.spacePlans().get(space.id) ?? [];
        const pickedId = this.comparedPlanIds().get(space.id);
        // Until the plans load, or while the user has not picked one, the column keeps the
        // KPIs the list already carries, so the default needs no extra request. Those are
        // also the freshest copy of the default plan's KPIs, so picking it back reads them.
        const picked = plans.find((plan) => plan.id === pickedId);
        const pickedOther = picked !== undefined && picked.id !== space.kpiPlanId;
        return {
          id: space.id,
          label: space.name,
          sublabel: `${space.nbPlans} ${this.translate.instant('features.planning_space_list.table.plans')}`,
          rawKpis: pickedOther ? picked.kpis : space.kpis,
          choices: plans.map((plan) => ({
            id: plan.id,
            label: plan.name,
            meta: plan.isDeployed
              ? this.translate.instant('features.plan.deploy.status.deployed')
              : undefined
          })),
          choiceId: picked?.id ?? space.kpiPlanId
        };
      })
    );
  });

  protected onCompareChoice({ itemId, choiceId }: KpiComparisonChoice): void {
    this.comparedPlanIds.update((picked) => new Map(picked).set(itemId, choiceId));
  }

  protected searchPlaceholder = computed(() => {
    this.currentLang(); // Trigger reactivity when language changes
    return this.translate.instant('common.search_placeholder');
  });

  protected search = signal<string>('');
  protected onlySelected = signal<boolean>(false);
  protected reloadPage = signal<boolean>(false);

  protected advancedFilters = signal<FilterConfiguration | null>(null);
  protected showAdvancedFiltering = signal<boolean>(false);

  protected openFilterForField = signal<string | null>(null);

  private sortState = signal<SortState<PlanningSpaceMetadataModel>[]>([]);
  protected presetSort = signal<SortState<PlanningSpaceMetadataModel>[]>([]);

  protected availableAdvancedFilters = computed<AvailableFilterField[]>(() => {
    this.currentLang(); // Only the labels are translated; the field identities are not
    return makePlanningSpaceFilterFields(this.translate);
  });

  /** Sort in the shape the API and a stored preset use: DB column names, not column keys. */
  private orderBy = computed<OrderBy[]>(() =>
    this.sortState().flatMap((sort) => {
      const dbColName = PLANNING_SPACE_SORTABLE_COLUMNS[sort.column];
      return isNil(dbColName) ? [] : [{ field: dbColName, order: sort.direction }];
    })
  );

  protected filterPresetPayload = computed<FilterPresetPayload>(() => {
    const filters = this.advancedFilters();
    const search = this.search().trim();
    const sort = this.orderBy();
    return {
      ...(!isNil(filters) && countFilterConditions(filters) > 0 ? { filters } : {}),
      ...(search ? { search } : {}),
      ...(sort.length > 0 ? { sort } : {})
    };
  });

  // Use the new Pagination class
  protected pagination = new Pagination(1, 25, 0);

  private paginationState = computed<PaginationState>(() => ({
    currentPage: this.pagination.currentPage(),
    itemsPerPage: this.pagination.itemsPerPage(),
    totalItems: this.pagination.totalItems(),
    onlySelected: this.onlySelected(),
    selectedItemsCount: this.selectedItemIds().size
  }));

  protected newPlanModalIsOpen = signal<boolean>(false);
  /** The raw rejection, mapped onto fields by the form that owns the schema. */
  protected newPlanSubmissionError = signal<unknown>(null);
  protected updatePlanModalIsOpen = signal<boolean>(false);
  protected deletePlanningSpaceModal = signal<{
    isOpen: boolean;
    config: ModalConfig;
    title: Signal<string>;
    message: Signal<string>;
  }>({
    isOpen: false,
    config: {
      icon: 'warning',
      type: ModalType.ERROR,
      dismissible: true,
      closeOnBackdropClick: true,
      askingModal: true,
      actions: [
        {
          label: this.translate.instant('common.cancel'),
          callback: () => {
            this.deletePlanningSpaceModal.set({
              ...this.deletePlanningSpaceModal(),
              isOpen: false
            });
          },
          color: 'cancel'
        },
        {
          label: this.translate.instant('common.delete'),
          callback: () => {
            this.submitDeletePlanningSpace(Array.from(this.selectedItemIds()));
            this.deletePlanningSpaceModal.set({
              ...this.deletePlanningSpaceModal(),
              isOpen: false
            });
          },
          color: 'danger',
          icon: 'trash'
        }
      ]
    },
    title: computed(() =>
      this.translate.instant('features.planning_space_list.delete.title')
    ),
    message: computed(() =>
      this.translate.instant('features.planning_space_list.delete.description')
    )
  });

  private search$ = toObservable(this.search);
  private pageSelector$ = toObservable(this.pagination.currentPage);
  private onlySelected$ = toObservable(this.onlySelected);
  private selectedItemIds$ = toObservable(this.selectedItemIds);
  private reloadPage$ = toObservable(this.reloadPage);
  private advancedFilters$ = toObservable(this.advancedFilters);
  private orderBy$ = toObservable(this.orderBy);
  private hideInternalSpaces$ = toObservable(this.hideInternalSpaces);

  private rawPlanningSpaces = toSignal(
    combineLatest([
      this.store.select(selectDataspaceModel).pipe(filter((x) => !isNil(x))),
      this.search$,
      this.pageSelector$,
      this.onlySelected$,
      this.selectedItemIds$,
      this.reloadPage$,
      this.advancedFilters$,
      this.orderBy$,
      this.hideInternalSpaces$
    ]).pipe(
      switchMap(([dataspace, searchTerm]) => {
        // Read off the signals rather than the tuple: the streams above are only there to
        // trigger the refetch, and positional destructuring of nine of them is a trap.
        const filters = pruneUnfilledConditions(this.advancedFilters());
        const orderBy = this.orderBy();
        return this.planningSpaceService
          .fetchList$({
            take: this.onlySelected()
              ? this.pagination.totalItems()
              : this.pagination.itemsPerPage(),
            page: this.onlySelected() ? 1 : this.pagination.currentPage(),
            dataSpaceId: dataspace.id,
            search: searchTerm,
            ...(isNil(filters) ? {} : { filters }),
            ...(orderBy.length > 0 ? { orderBy } : {}),
            ...(this.hideInternalSpaces() ? { hideInternal: true } : {})
          })
          .pipe(
            map((res) => {
              this.pagination.updateTotalItems(res.total);
              return res;
            }),
            // `toSignal` stores a stream error and replays it on every read, and this signal
            // is read from the template — so an unrecovered rejection throws on every change
            // detection pass from then on, which hangs the page rather than showing an error.
            //
            // Recovering inside the switchMap ends the failed request alone: catching on the
            // outer stream would complete it, leaving every later filter or page change
            // unfetched.
            catchError((error: unknown) => {
              console.error('Error fetching planning spaces:', error);
              this.pagination.updateTotalItems(0);
              return of<FetchPlanningSpaceListResponse>({ total: 0, page: 1, result: [] });
            })
          );
      })
    )
  );

  private dataspace = toSignal(
    this.store.select(selectDataspaceModel).pipe(filter((x) => !isNil(x)))
  );

  private fetchRecipes = toSignal(
    toObservable(this.dataspace).pipe(
      filter((x) => !isNil(x)),
      switchMap((dataspace) => this.recipeService.getListRecipes$(dataspace.id))
    )
  );

  protected planningSpaces = computed(() => {
    const overrides = this.deployOverrides();
    const items = this.processedList().displayedItems;
    if (Object.keys(overrides).length === 0) return items;

    // Apply live websocket overrides on top of fetched rows. We intentionally do NOT
    // trigger a reload here: overrides may reference plans not on this page (other
    // planning spaces, plans the user can't see), and reacting to that from inside a
    // computed produced an unbounded zero-delay refetch loop.
    // Unlike the plan graph, this planning-space rollup intentionally does NOT
    // synthesize rows for override configs missing from lastDeployStatus — such a
    // row would have no real planning-space context.
    return items.map((ps) => ({
      ...ps,
      lastDeployStatus: ps.lastDeployStatus?.map((ds) => {
        const key = `${ds.planId}__${ds.configId}`;
        const ov = overrides[key];
        const fetchedAt = ds.updatedAt ? new Date(ds.updatedAt).getTime() : 0;
        return ov && ov.updatedAt > fetchedAt ? { ...ds, status: ov.status } : ds;
      })
    }));
  });

  protected paginationConfig = computed<PaginationConfig>(() =>
    this.pagination.createPaginationConfig(this.paginationState())
  );

  protected currentLang = toSignal(this.translate.onLangChange);

  protected columns = computed<TableColumn<PlanningSpaceMetadataModel>[]>(() => {
    this.currentLang();
    // Rebuilt when the channel count lands, or the deploy column would keep rendering
    // from the zero it started with.
    this.deployChannels.enabledCount();
    // Likewise for the tags, or their pills would stay grey until the next rebuild.
    this.availableTags();
    const activeFields = collectActiveFilterFields(this.advancedFilters());

    return this.buildTableColumns().map((column) => {
      const filterField = PLANNING_SPACE_FILTERABLE_COLUMNS[column.key];
      return {
        ...column,
        config: {
          ...column.config,
          filterable: !isNil(filterField),
          sortable: !isNil(PLANNING_SPACE_SORTABLE_COLUMNS[column.key])
        },
        filtered: !isNil(filterField) && activeFields.has(filterField)
      };
    });
  });

  protected actions = signal<TableAction<PlanningSpaceMetadataModel>[]>([
    {
      label: 'open',
      icon: ICONS.arrowSquareOut,
      color: 'primary',
      action: (row: PlanningSpaceMetadataModel): void => {
        this.router.navigate([row.id], { relativeTo: this.activatedRoute });
      }
    }
  ]);

  protected tableConfig = computed<TableConfig<PlanningSpaceMetadataModel>>(() => ({
    rowId: 'id',
    bordered: true,
    stickyHeader: true,
    sortable: true,
    horizontalScroll: true,
    doubleClickAction: (row: PlanningSpaceMetadataModel): void => {
      this.router.navigate([row.id], { relativeTo: this.activatedRoute });
    },
    columnActionSettings: {
      sortable: true,
      filterable: true,
      resizeable: true,
      groupable: false,
      movable: false,
      hideable: false
    },
    selection: {
      enabled: true,
      multiple: true,
      mode: 'checkbox',
      action: (row: PlanningSpaceMetadataModel) => this.toggleRowSelection(row)
    }
  }));

  protected readonly newPlanningSapceModalTitleKey =
    'features.planning_space_list.new_from_recipe.title';
  protected readonly updatePlanningSapceModalTitleKey =
    'features.planning_space_list.update.title';
  protected updatePlanningSapceModalTitle = computed(() => {
    this.currentLang();
    return this.translate.instant(this.updatePlanningSapceModalTitleKey);
  });
  protected newPlanningSapceModalTitle = computed(() => {
    this.currentLang();
    return this.translate.instant(this.newPlanningSapceModalTitleKey);
  });

  protected newPlanningSpaceModalConfig = computed<ModalConfig>(() => {
    this.currentLang();
    return {
      dismissible: true,
      closeOnBackdropClick: true,
      icon: 'plus',
      actions: [
        {
          label: this.translate.instant('common.cancel'),
          callback: () => {
            this.newPlanModalIsOpen.set(false);
          },
          color: 'cancel'
        },
        {
          label: this.translate.instant('common.submit'),
          callback: () => {
            if (this.newPlanningSpaceComponentRef?.isFormValid()) {
              this.newPlanningSpaceComponentRef.onSubmit();
            }
          },
          icon: 'checkCircle',
          color: 'primary'
        }
      ]
    };
  });
  protected updatePlanningSpaceModalConfig: ModalConfig = {
    dismissible: true,
    closeOnBackdropClick: true
  };

  protected newPlanningSpaceConfig = computed<NewPlanningSpaceConfig>(() => ({
    selectedRecipeId: 0,
    availableRecipes: this.fetchRecipes() ?? []
  }));

  protected updatePlanningSpaceConfig = computed<UpdatePlanningSpaceConfig>(() => {
    const selected = Array.from(this.selectedItems());
    if (selected.length === 1) {
      const space = selected[0];
      return {
        planningSpace: {
          id: space.id,
          name: space.name,
          description: space.description,
          tags: space.tags
        }
      };
    }
    return {
      planningSpace: {
        id: '',
        name: '',
        description: '',
        tags: []
      }
    };
  });

  protected toolboxSections = computed(() => this.buildToolboxSections());

  constructor() {
    effect(() => {
      const currentPage = this.pagination.currentPage();
      const normalizedPage = this.pagination.normalizePage(currentPage);

      if (currentPage !== normalizedPage) {
        this.pagination.setPage(normalizedPage);
      }
    });

    // An open comparison with nothing left to compare would keep half the viewport while the
    // user is reaching for the very rows it is covering.
    effect(() => {
      if (this.compareOpen() && !this.canCompare()) {
        untracked(() => this.compareOpen.set(false));
      }
    });

    effect(() => {
      if (!this.compareOpen()) {
        untracked(() => this.dropSpacePlans());
        return;
      }
      for (const space of this.selectedItems()) {
        const version = spacePlansVersion(space);
        if (space.nbPlans < 2 || this.requestedSpacePlans.get(space.id) === version)
          continue;
        this.fetchSpacePlans(space.id, version);
      }
    });

    effect(() => {
      this.filterPresetsStore.entityType.set(PLANNING_SPACE_PRESET_ENTITY_TYPE);
      this.filterPresetsStore.dataSpaceId.set(this.dataspace()?.id);
      this.filterPresetsStore.availableFields.set(this.availableAdvancedFilters());
      this.filterPresetsStore.sortableFields.set(
        makePlanningSpaceSortableFields(this.translate)
      );
      this.filterPresetsStore.currentPayload.set(this.filterPresetPayload());
    });

    this.filterPresetsStore.applied
      .pipe(takeUntilDestroyed())
      .subscribe((applied) => this.onFilterPresetApplied(applied));
  }

  OnSearchTermChange(event: string): void {
    Pagination.handleSearch(
      event,
      (term) => this.search.set(term),
      () => this.pagination.resetToFirstPage()
    );
  }

  /**
   * Filtering changes which spaces exist, so the page in view may no longer hold any: without
   * the reset, narrowing the filter while on a later page leaves the table empty. Detaching
   * the active preset is what stops an edited tree from still being labelled with its name.
   */
  onFilterChanges(config: FilterConfiguration): void {
    this.advancedFilters.set(config);
    this.filterPresetsStore.detach();
    this.pagination.resetToFirstPage();
    this.clearSelection();
  }

  /**
   * Seeds a condition on the clicked column and pops its editor open, so the header's
   * "Filter" entry lands the user on something to fill in rather than silently applying an
   * empty predicate. The filter bar is forced open because it is where that editor lives:
   * left closed, the condition would be applied out of sight.
   */
  protected addFilterOn(column: keyof PlanningSpaceMetadataModel): void {
    const fieldName = PLANNING_SPACE_FILTERABLE_COLUMNS[column];
    if (isNil(fieldName)) return;

    const condition = createFilterCondition(
      findFilterField(this.availableAdvancedFilters(), fieldName)
    );
    if (isNil(condition)) return;

    this.showAdvancedFiltering.set(true);
    this.onFilterChanges(addOrReplaceConditionInConfig(this.advancedFilters(), condition));

    this.openFilterForField.set(condition.id);
    setTimeout(() => this.openFilterForField.set(null), 100);
  }

  protected removeFilterOn(column: keyof PlanningSpaceMetadataModel): void {
    const fieldName = PLANNING_SPACE_FILTERABLE_COLUMNS[column];
    if (isNil(fieldName)) return;

    this.onFilterChanges(removeConditionFromConfig(this.advancedFilters(), fieldName));
  }

  /**
   * The list is paged server-side, so the order has to be re-fetched rather than applied to
   * the rows already on screen. The page is deliberately kept: sorting reorders the result
   * set without changing its size, so the page in view still holds rows.
   */
  protected onSortChange(sortState: SortState<PlanningSpaceMetadataModel>[]): void {
    this.sortState.set(sortState);
  }

  /**
   * Restoring a preset writes `advancedFilters` directly, leaving the store attached so its
   * chip stays selected. `droppedFields` is not surfaced: this list's filterable fields are
   * fixed rather than schema-derived, so a stored condition can never fail to resolve.
   */
  private onFilterPresetApplied(applied: AppliedFilterPreset): void {
    this.advancedFilters.set(applied.filters ?? null);
    this.search.set(applied.search ?? '');

    if (!isNil(applied.filters)) {
      this.showAdvancedFiltering.set(true);
    }

    // A preset without a stored sort leaves the current ordering alone; only one that
    // carries a sort re-seeds the table, which then reports it back through `onSortChange`.
    if (!isNil(applied.sort)) {
      this.presetSort.set(applied.sort as SortState<PlanningSpaceMetadataModel>[]);
    }

    this.pagination.resetToFirstPage();
    this.clearSelection();
  }

  onPageChange(newPage: number): void {
    this.pagination.setPage(newPage);
  }

  onSelectOnly(selected: boolean): void {
    Pagination.handleOnlySelectedToggle(
      selected,
      (value) => this.onlySelected.set(value),
      () => this.pagination.resetToFirstPage()
    );
  }

  hasSelection(): boolean {
    return this.selectedItemIds().size > 0;
  }

  clearSelection(): void {
    this.selectedItemIds.set(new Set());
  }

  showPageSelector = computed(() => this.pagination.shouldShowPagination());

  onSelect(): number {
    return this.selectedItemIds().size;
  }

  showSelect(): boolean {
    return this.hasSelection();
  }

  /**
   * Sequenced rather than fanned out: each delete now retires every plan of the space in
   * the data layer inside a transaction of up to 30 s, and each is judged on its own, so
   * parallel calls pile up long transactions and their outcomes arrive interleaved with
   * no way to say which space each one belongs to.
   */
  submitDeletePlanningSpace(itemIds: string[]): void {
    if (itemIds.length === 0) {
      return;
    }
    // Captured before the first delete: the selection is cleared and the list refetched
    // once the batch is done, and the report names the spaces the user acted on.
    const names = new Map(
      Array.from(this.selectedItems()).map((space) => [space.id, space.name])
    );

    from(itemIds)
      .pipe(
        concatMap((id) =>
          this.planningSpaceService.deletePlanningSpace$(id).pipe(
            map(() => ({ id, error: undefined as unknown })),
            catchError((error: unknown) => of({ id, error }))
          )
        ),
        toArray()
      )
      .subscribe((results) => {
        this.reportDeletion(results, names);
        this.reloadPage.set(!this.reloadPage());
        this.pagination.resetToFirstPage();
        this.clearSelection();
      });
  }

  /** One toast for what went, one naming each space that did not and why. */
  private reportDeletion(
    results: { id: string; error: unknown }[],
    names: Map<string, string>
  ): void {
    const title = this.translate.instant('features.planning_space_list.delete.title');
    const failed = results.filter((result) => result.error !== undefined);

    if (failed.length < results.length) {
      this.notificationService.success(
        title,
        this.translate.instant('features.planning_space_list.delete.success', {
          count: results.length - failed.length
        })
      );
    }
    if (failed.length === 0) {
      return;
    }

    for (const failure of failed) {
      console.error(`Error deleting planning space with ID ${failure.id}:`, failure.error);
    }
    const message = failed
      .map((failure) =>
        this.translate.instant(this.deleteErrorKey(failure.error), {
          name: names.get(failure.id) ?? failure.id
        })
      )
      .join(' ');
    this.notificationService.error(title, message);
  }

  /**
   * The one refusal worth its own copy is a live optimization: GBE is the only side
   * guarding it — the data layer dropped its own guard, because a worker killed mid-run
   * left a plan permanently undeletable there — and it guards the whole space, not just
   * the plan the user can see running.
   */
  private deleteErrorKey(error: unknown): string {
    const errorCode = (error as GbeError | undefined)?.errorCode;
    return errorCode === GbeErrorCodes.PLANNING_SPACE_HAS_RUNNING_OPTIMIZATIONS
      ? 'features.planning_space_list.delete.errors.planning_space_has_running_optimizations'
      : 'features.planning_space_list.delete.errors.unknown';
  }

  submitNewPlanningSpace(data: NewPlanningSpaceOutputData): void {
    const dataSpaceId = this.dataspace()?.id ?? 0;
    const request: AddPlanningSpaceRequest = {
      name: data.name,
      description: data.description,
      tags: data.tags,
      recipeId: Number(data.recipeId),
      solverId: Number(data.solverId),
      dataSpaceId: dataSpaceId,
      // Sent only when set, so an ordinary creation keeps its current payload exactly.
      ...(data.isInternal && { isInternal: true }),
      ...(data.parameters !== undefined && { parameters: data.parameters })
    };
    this.newPlanSubmissionError.set(null);
    this.planningSpaceService
      .addPlanningSpace$(request)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.newPlanModalIsOpen.set(false);
          // Land on what the planner just asked for. Leaving the route makes a list
          // refresh pointless: coming back re-creates the component, which re-fetches.
          this.router.navigate([response.planningSpace.id, 'plan', response.plan.id], {
            relativeTo: this.activatedRoute
          });
        },
        error: (error) => {
          // A parameter rejection must keep the modal open so the planner can
          // fix the values; anything else keeps the previous behaviour. The form
          // maps the details onto its fields, since it owns the schema.
          if (isRecipeParameterError(error)) {
            this.newPlanSubmissionError.set(error);
            return;
          }
          this.newPlanModalIsOpen.set(false);
          console.error('Error adding planning space:', error);
        }
      });
  }

  submitUpdatePlanningSpace(data: {
    id: string;
    name: string;
    description: string;
    tags: string[];
  }): void {
    this.planningSpaceService
      .updatePlanningSpace$(data.id, {
        name: data.name,
        description: data.description,
        tags: data.tags
      })
      .pipe(
        tap(() => {
          this.updatePlanModalIsOpen.set(false);
        })
      )
      .subscribe({
        next: () => {
          this.reloadPage.set(!this.reloadPage());
          this.clearSelection();
        },
        error: (error) => {
          console.error('Error updating planning space:', error);
        }
      });
  }

  private toggleRowSelection(row: PlanningSpaceMetadataModel): void {
    Pagination.handleRowSelection(row, (space) => space.id, this.selectedItemIds(), {
      updateSelection: (selection) => this.selectedItemIds.set(selection),
      updateOnlySelected: (value) => this.onlySelected.set(value)
    });
  }

  private processedList = computed(() =>
    Pagination.processListPagination(
      this.rawPlanningSpaces()?.result ?? [],
      this.selectedItemIds(),
      this.paginationState(),
      (space) => space.id
    )
  );

  searchCfg: SearchConfig = {
    active: true,
    type: 'simple',
    shortcut: true
  };

  private selectRecipe(recipeId: number): void {
    this.newPlanningSpaceConfig().selectedRecipeId = recipeId;
    this.newPlanModalIsOpen.set(true);
  }

  private buildToolboxSections(): ToolboxSection[] {
    this.currentLang();
    return [
      {
        id: 'selection-actions',
        items: [
          {
            id: 'update',
            icon: ICONS.pencil,
            tooltip: this.translate.instant('features.planning_space_list.update.title'),
            disabled: this.selectedItems().size !== 1,
            callback: () => {
              if (this.selectedItems().size === 1) {
                this.updatePlanModalIsOpen.set(true);
              }
            }
          },
          {
            id: 'compare-kpis',
            icon: ICONS.scales,
            active: this.compareOpen(),
            disabled: !this.canCompare(),
            tooltip: this.translate.instant(
              this.canCompare()
                ? 'features.planning_space_list.compare.tooltip'
                : 'features.planning_space_list.compare.disabled_tooltip'
            ),
            callback: () => this.compareOpen.update((open) => !open)
          }
        ]
      },
      {
        id: 'bulk-actions',
        items: [
          {
            id: 'delete',
            icon: ICONS.trash,
            tooltip: this.translate.instant('features.planning_space_list.delete.title'),
            disabled: !this.hasSelection(),
            callback: () => {
              if (this.hasSelection()) {
                this.deletePlanningSpaceModal.set({
                  ...this.deletePlanningSpaceModal(),
                  isOpen: true
                });
              }
            }
          }
        ]
      },
      {
        id: 'default actions',
        items: [
          {
            id: 'new-plan',
            label: this.translate.instant(
              'features.planning_space_list.toolbox.buttons.new_plan.title'
            ),
            icon: ICONS.plus,
            callToAction: true,
            tooltip: this.translate.instant(
              'features.planning_space_list.toolbox.buttons.new_plan.tooltip'
            ),
            items: this.newPlanningSpaceConfig().availableRecipes.map((r) => ({
              label: r.name,
              action: () => {
                this.selectRecipe(r.id);
              }
            }))
          }
        ]
      }
    ];
  }

  private buildTableColumns(): TableColumn<PlanningSpaceMetadataModel>[] {
    return [
      {
        key: 'name',
        config: { resizeable: true },
        label: this.translate.instant('common.name'),
        // The badge needs no role check of its own: GBE returns an internal space only to
        // the roles allowed to see one, so `isInternal` is never set for anyone else.
        type: 'string-with-badge',
        width: 200,
        renderHook: (
          value: unknown,
          row?: PlanningSpaceMetadataModel
        ): StringWithBadgeCellValue => ({
          text: (value as string) ?? '',
          ...(row?.isInternal === true && {
            badge: this.translate.instant('features.planning_space_list.internal.badge'),
            badgeStatus: 'pending' as const
          })
        })
      },
      {
        key: 'description',
        config: { resizeable: true },
        label: this.translate.instant('common.description'),
        type: 'string'
      },
      {
        key: 'nbPlans',
        config: { resizeable: true },
        label: this.translate.instant('features.planning_space_list.table.plans'),
        type: 'number',
        width: 150
      },
      {
        key: 'createdAt',
        config: { resizeable: true },
        label: this.translate.instant('common.created'),
        type: 'datetime',
        width: 150
      },
      {
        key: 'updatedAt',
        config: { resizeable: true },
        label: this.translate.instant('common.modified'),
        type: 'datetime',
        width: 150
      },
      {
        key: 'tags',
        config: { resizeable: true },
        label: this.translate.instant('common.tags'),
        type: 'tag-list',
        width: 200,
        renderHook: (value: unknown) => renderTagList(value, this.availableTags())
      },
      {
        key: 'kpis',
        config: { resizeable: true },
        label: this.translate.instant('features.planning_space_list.table.kpis'),
        type: 'custom',
        width: 200,
        cellTemplate: 'custom',
        renderHook: (value: unknown) => this.formatKpiSummary(value as RawKpi[] | undefined)
      },
      {
        key: 'lastDeployStatus',
        label: this.translate.instant('features.plan.deploy.status_detail'),
        type: 'deploy-status-overall',
        width: 110,
        // GBE answers with one plan's statuses — the space's most recently deployed plan
        // (`list-planning-spaces.usecase`) — so the same coverage rule as the plan list
        // applies: every channel out, or the deploy is still in progress.
        renderHook: (_value: unknown, row?: PlanningSpaceMetadataModel) => ({
          statuses: row?.lastDeployStatus ?? [],
          expectedChannels: this.deployChannels.enabledCount()
        })
      }
    ];
  }

  private fetchSpacePlans(spaceId: string, version: string): void {
    this.requestedSpacePlans.set(spaceId, version);
    this.planningSpaceService
      .fetchEveryPlanOfPlanningSpace$(spaceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (plans) => {
          // A response for a version since superseded, or for a cache dropped on close,
          // would put back exactly the stale plans the newer request is replacing.
          if (this.requestedSpacePlans.get(spaceId) !== version) return;
          this.spacePlans.update((cache) => new Map(cache).set(spaceId, plans));
        },
        // The column keeps its default plan; forgetting the request lets the next selection
        // change or reopening retry.
        error: (error: unknown) => {
          console.error('Error fetching the plans of a planning space:', error);
          if (this.requestedSpacePlans.get(spaceId) === version) {
            this.requestedSpacePlans.delete(spaceId);
          }
        }
      });
  }

  private dropSpacePlans(): void {
    this.requestedSpacePlans.clear();
    if (this.spacePlans().size > 0) this.spacePlans.set(new Map());
  }

  private formatKpiSummary(rawKpis: RawKpi[] | undefined): string {
    return this.kpiComparison
      .toKpis(rawKpis)
      .map((kpi) => {
        const { prefix = '', suffix = '' } = kpi.metadata;
        return `${this.translate.instant(kpi.i18nKey)}: ${prefix}${kpi.value}${suffix}`;
      })
      .join(', ');
  }
}
